import { EntityId } from "../ids";

/** Construction-time defaults shared by authored enemies and transient entities. */

/**
 * A value the original randomised at construction time. Resolves to
 * `(Math.ceil(Math.random() * spread) + offset) * scale + base`, which is the
 * exact shape every one of those expressions had.
 */
export interface RandomRange {
  readonly spread: number;
  readonly offset?: number;
  readonly scale?: number;
  readonly base?: number;
}

/** Melee tuning for the enemies that charge and slash. */
export interface SlashTuning {
  readonly animOffset: number;
  readonly frameLimit: number;
  readonly freezeFrame: number;
  readonly slashRange: number;
  readonly freq: number;
}

/** Set together: an object that moves aims itself at its spawn angle. */
export interface ObjectMotion {
  readonly speed: number | RandomRange;
  /** Spread applied to the spawn angle before the speed vector is taken. */
  readonly angleJitter?: RandomRange;
  /** Splits the pair 45 degrees either side, the second one flagged. */
  readonly fork?: boolean;
}

export interface ProjectileStats {
  readonly damage: number;
  readonly lifeLimit: number | RandomRange;
  readonly hitRadius?: number;
}

export interface ObjectStats {
  readonly tileSize: number;
  readonly bitmapWidth: number;
  readonly frameLimit: number;
  readonly animSpeed: number;
  readonly animLoop?: boolean;
  readonly animOffset?: number;
  /** Starts on one of the first few frames rather than frame 0. */
  readonly frame?: RandomRange;
  /** Starts on a random frame of the whole strip. */
  readonly randomFrame?: boolean;
  readonly motion?: ObjectMotion;
  readonly projectile?: ProjectileStats;
}

/** Drawn for every enemy before its id is looked at, so the roll order is fixed. */
export const ENEMY_INTRO_COUNTER: RandomRange = { spread: 30, base: -30 };

/** Melee tuning for enemies that have none; these fields are never read there. */
export const SLASH_NONE: SlashTuning = {
  animOffset: 0,
  frameLimit: 0,
  freezeFrame: 0,
  slashRange: 0,
  freq: 0,
};

/** Compiled effects and projectiles, keyed by their runtime id. */
export const STATIC_ENTITY_STATS: Readonly<
  Partial<Record<EntityId, ObjectStats>>
> = {
  [EntityId.Spark]: {
    tileSize: 64,
    bitmapWidth: 320,
    frameLimit: 10,
    animSpeed: 1,
  },
  [EntityId.Explosion]: {
    tileSize: 64,
    bitmapWidth: 256,
    frameLimit: 12,
    animSpeed: 2,
  },
  [EntityId.MovingExplosion]: {
    tileSize: 32,
    bitmapWidth: 128,
    frameLimit: 16,
    animSpeed: 2,
    motion: { speed: { spread: 100, scale: 0.01, base: 1.5 } },
  },
  [EntityId.OrbShot]: {
    tileSize: 16,
    bitmapWidth: 48,
    frameLimit: 9,
    animSpeed: 2,
    animLoop: true,
    randomFrame: true,
    motion: { speed: 3.5 },
    projectile: {
      damage: 5,
      hitRadius: 4,
      lifeLimit: { spread: 90, base: 160 },
    },
  },
  [EntityId.ShotAbsorb]: {
    tileSize: 48,
    bitmapWidth: 144,
    frameLimit: 9,
    animSpeed: 2,
  },
  [EntityId.SmallWarp]: {
    tileSize: 64,
    bitmapWidth: 512,
    frameLimit: 32,
    animSpeed: 2,
  },
  [EntityId.EnemySwipeA]: {
    tileSize: 64,
    bitmapWidth: 320,
    frameLimit: 9,
    animSpeed: 1,
  },
  [EntityId.EnemySwipeB]: {
    tileSize: 64,
    bitmapWidth: 320,
    frameLimit: 9,
    animSpeed: 1,
    animOffset: 10,
  },
  [EntityId.Reserved18]: {
    tileSize: 64,
    bitmapWidth: 512,
    frameLimit: 15,
    animSpeed: 1,
  },
  [EntityId.Reserved19]: {
    tileSize: 64,
    bitmapWidth: 512,
    frameLimit: 15,
    animSpeed: 1,
    animOffset: 16,
  },
  [EntityId.Reserved20]: {
    tileSize: 64,
    bitmapWidth: 512,
    frameLimit: 15,
    animSpeed: 1,
    animOffset: 32,
  },
  [EntityId.Reserved21]: {
    tileSize: 64,
    bitmapWidth: 512,
    frameLimit: 15,
    animSpeed: 1,
    animOffset: 48,
  },
  [EntityId.Reserved22]: {
    tileSize: 64,
    bitmapWidth: 512,
    frameLimit: 15,
    animSpeed: 1,
    animOffset: 64,
  },
  [EntityId.SwordCharge]: {
    tileSize: 32,
    bitmapWidth: 128,
    frameLimit: 12,
    animSpeed: 2,
  },
  [EntityId.EnemyESlashA]: {
    tileSize: 128,
    bitmapWidth: 512,
    frameLimit: 11,
    animSpeed: 2,
  },
  [EntityId.EnemyESlashB]: {
    tileSize: 128,
    bitmapWidth: 512,
    frameLimit: 11,
    animSpeed: 2,
    animOffset: 12,
  },
  [EntityId.LargeSwordCharge]: {
    tileSize: 64,
    bitmapWidth: 256,
    frameLimit: 12,
    animSpeed: 1,
    frame: { spread: 3, base: -1 },
  },
  [EntityId.RotatingLaser]: {
    tileSize: 32,
    bitmapWidth: 192,
    frameLimit: 2,
    animSpeed: 2,
    animLoop: true,
    motion: { speed: 3.5 },
    projectile: { damage: 5, lifeLimit: 200 },
  },
  [EntityId.LargeWarp]: {
    tileSize: 128,
    bitmapWidth: 1024,
    frameLimit: 32,
    animSpeed: 2,
  },
  [EntityId.LargeExplosion]: {
    tileSize: 128,
    bitmapWidth: 512,
    frameLimit: 12,
    animSpeed: 2,
  },
  [EntityId.Smoke]: {
    tileSize: 48,
    bitmapWidth: 192,
    frameLimit: 12,
    animSpeed: 3,
    motion: {
      speed: 1.15,
      angleJitter: { spread: 100, offset: -50, scale: 0.35 },
    },
  },
  [EntityId.SickleShot]: {
    tileSize: 32,
    bitmapWidth: 128,
    frameLimit: 12,
    animSpeed: 1,
    animLoop: true,
    randomFrame: true,
    motion: { speed: 4.85, fork: true },
    projectile: { damage: 6, hitRadius: 18, lifeLimit: 145 },
  },
  [EntityId.HugeExplosion]: {
    tileSize: 96,
    bitmapWidth: 384,
    frameLimit: 12,
    animSpeed: 2,
  },
  [EntityId.Missile]: {
    tileSize: 48,
    bitmapWidth: 288,
    frameLimit: 2,
    animSpeed: 2,
    animLoop: true,
    motion: { speed: { spread: 25, scale: 0.1, base: 2.75 } },
    projectile: { damage: 14, hitRadius: 24, lifeLimit: 245 },
  },
  [EntityId.LaserShot]: {
    tileSize: 32,
    bitmapWidth: 192,
    frameLimit: 2,
    animSpeed: 2,
    animLoop: true,
    motion: { speed: 5.75 },
    projectile: { damage: 5, lifeLimit: 200 },
  },
};

/** Resolves a table field that is either a constant or a construction-time roll. */
export function rollValue(v: number | RandomRange): number {
  if (typeof v === "number") {
    return v;
  }
  return (
    (Math.ceil(Math.random() * v.spread) + (v.offset ?? 0)) * (v.scale ?? 1) +
    (v.base ?? 0)
  );
}
